import type { RenderPackBytesPayload } from './types'

type RecordLike = Record<string, unknown>

function asRecord(value: unknown): RecordLike | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordLike)
    : null
}

function firstRecord(source: unknown, keys: string[]): RecordLike | null {
  const record = asRecord(source)
  if (!record) return null
  if (typeof record.transport === 'string' && asRecord(record.blocks)) return record
  for (const key of keys) {
    const candidate = asRecord(record[key])
    if (candidate) return candidate
  }
  return null
}

function decodeBase64Bytes(value: string): number[] | null {
  try {
    const binary = globalThis.atob(value)
    const bytes: number[] = []
    for (let index = 0; index < binary.length; index += 1) {
      bytes.push(binary.charCodeAt(index))
    }
    return bytes
  } catch {
    return null
  }
}

function normalizeNumberBytes(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null
  const bytes: number[] = []
  for (const item of value) {
    if (!Number.isInteger(item) || item < 0 || item > 255) return null
    bytes.push(item)
  }
  return bytes
}

/**
 * Extract Studio's published RPK1 payload from either the full published schema
 * object or the nested renderPack/renderPackPublishPayload object.
 */
export function decodeRenderPackPublishPayload(source: unknown): RenderPackBytesPayload | undefined {
  const payload = firstRecord(source, ['renderPackPublishPayload', 'renderPackPackaging', 'renderPack'])
  if (!payload) return undefined
  if (payload.transport !== 'RPK1') return undefined

  const blocks = asRecord(payload.blocks)
  if (!blocks) return undefined

  const decoded: RenderPackBytesPayload = {}
  for (const [blockName, rawBlock] of Object.entries(blocks)) {
    const block = asRecord(rawBlock)
    if (!block) continue

    if (block.encoding === 'base64' && typeof block.bytes === 'string') {
      const bytes = decodeBase64Bytes(block.bytes)
      if (bytes && bytes.length > 0) decoded[blockName] = bytes
      continue
    }

    const bytes = normalizeNumberBytes(block.bytes)
    if (bytes && bytes.length > 0) decoded[blockName] = bytes
  }

  return Object.keys(decoded).length > 0 ? decoded : undefined
}

export function renderPackWireBytesToUint8Array(
  renderPackBytes: RenderPackBytesPayload | undefined,
): Record<string, Uint8Array> | undefined {
  if (!renderPackBytes || Object.keys(renderPackBytes).length === 0) return undefined
  const normalized: Record<string, Uint8Array> = {}
  for (const [blockName, bytes] of Object.entries(renderPackBytes)) {
    const valid = normalizeNumberBytes(bytes)
    if (valid && valid.length > 0) normalized[blockName] = Uint8Array.from(valid)
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined
}
